import { Capacitor } from '@capacitor/core';

/**
 * Configuración centralizada del entorno
 * Detecta automáticamente si estamos en Web, PWA o Android/iOS (Capacitor)
 */

export const Environment = {
  /**
   * Detecta si estamos en plataforma nativa (Android/iOS)
   */
  isNative(): boolean {
    const platform = Capacitor.getPlatform();
    return platform === 'android' || platform === 'ios';
  },

  /**
   * Detecta si estamos en web (navegador)
   */
  isWeb(): boolean {
    return Capacitor.getPlatform() === 'web';
  },

  /**
   * Detecta si estamos en PWA (instalada)
   */
  isPWA(): boolean {
    if (typeof window === 'undefined') return false;
    
    // Detectar si está instalada como PWA
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://')
    );
  },

  /**
   * Retorna el platform actual
   */
  getPlatform(): 'android' | 'ios' | 'web' | 'pwa' {
    if (this.isNative()) {
      return Capacitor.getPlatform() as 'android' | 'ios';
    }
    if (this.isPWA()) {
      return 'pwa';
    }
    return 'web';
  },

  /**
   * Retorna la URL base de la aplicación
   * - En producción web/PWA: https://app.logrit.life
   * - En desarrollo web: http://localhost:5173
   * - En Android/iOS: capacitor://localhost (manejado internamente)
   */
  getBaseURL(): string {
    if (this.isNative()) {
      // En Capacitor, siempre usar el origen local
      return window.location.origin; // capacitor://localhost
    }

    // En web/PWA, usar la URL de producción o desarrollo
    if (import.meta.env.PROD) {
      return 'https://app.logrit.life';
    }

    // En desarrollo
    return window.location.origin; // http://localhost:5173
  },

  /**
   * Retorna una URL completa para una ruta
   * Útil para emails, notificaciones, etc.
   */
  getFullURL(path: string): string {
    const basePath = path.startsWith('/') ? path : `/${path}`;
    
    if (this.isNative()) {
      // Para links en notificaciones desde app nativa, usar URL pública
      return `https://app.logrit.life${basePath}`;
    }

    return `${this.getBaseURL()}${basePath}`;
  },

  /**
   * Retorna información de debug del entorno
   */
  getDebugInfo() {
    return {
      platform: this.getPlatform(),
      isNative: this.isNative(),
      isWeb: this.isWeb(),
      isPWA: this.isPWA(),
      baseURL: this.getBaseURL(),
      origin: window.location.origin,
      href: window.location.href,
      capacitorPlatform: Capacitor.getPlatform(),
    };
  },
};

